import * as Sentry from '@sentry/react'

export function initSentry() {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Enables Sentry Logs (https://docs.sentry.io/platforms/javascript/guides/react/logs/)
      // so we can ship structured diagnostic logs (e.g. sync conflicts) via
      // `Sentry.logger.warn(...)` distinct from the issues stream.
      enableLogs: true,
    })
  }
}

/** Set the authenticated user on the Sentry scope. */
export function setSentryUser(user: { id: string; email?: string | null; name?: string | null }): void {
  Sentry.setUser({
    id: user.id,
    ...(user.email ? { email: user.email } : {}),
    ...(user.name ? { username: user.name } : {}),
  })
}

/** Clear user context from Sentry (on logout). */
export function clearSentryUser(): void {
  Sentry.setUser(null)
}

/**
 * Ship a sync conflict to Sentry **Logs** (not Issues) at warning level.
 *
 * Used to investigate why conflicts fire — particularly the "single device should
 * never produce conflicts" symptom. `attributes` should be a flat map of
 * primitives (strings/numbers/booleans) carrying every diagnostic field
 * available at the moment of detection. Never include raw note title/content —
 * pass lengths and a quick non-cryptographic content fingerprint instead.
 *
 * See https://docs.sentry.io/platforms/javascript/guides/react/logs/.
 */
export function logSyncConflict(attributes: Record<string, string | number | boolean>): void {
  Sentry.logger.warn('sync_conflict_detected', attributes)
}

export { Sentry }
